import { useState, useEffect } from "react";
import "../../css/FormBrain.css";
import Form from "/src/components/form/Form";
import Field from "/src/components/UI/Field/Field";
import TextInput from "/src/components/UI/TextField/TextInput";
import ButtonAction from "/src/components/UI/Button/Button";
import { useForm } from "react-hook-form";
import brainService from "../../services/BrainService";
import DropDown from "/src/components/UI/DropDownModels/DropDown";

function FormBrain({ form, onSend }) {
    const data = {
        id: form.data.id,
        name: form.data.name,
        description: form.data.description,
        status: form.data.status || "private",
        model: form.data.brain_model,
        max_tokens: form.data.max_tokens || 2000,
    };

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        getValues,
        setValue,
        watch,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            name: data.name,
            description: data.description,
            status: data.status,
            model: data.model,
            max_tokens: data.max_tokens,
        },
    });

    const [models, setModels] = useState([]);
    const [loadAnim, setLoading] = useState(null);

    const model = watch("model", data.model);
    const maxTokens = watch("max_tokens", data.max_tokens);

    const defineMaxTokens = (modelName) => {
        const selectedModel = models.find((model) => model.value === modelName);
        return selectedModel ? selectedModel.maxOutput : 3000;
    };

    const handleModelChange = (selectedModel) => {
        setValue("model", selectedModel);
        const newMaxTokens = defineMaxTokens(selectedModel);
        setValue("max_tokens", newMaxTokens);
    };

    useEffect(() => {
        const fetchModels = async () => {
            const [models, error] = await brainService.getModels();

            if (error) {
                console.error("Error fetching models:", error);
                return;
            }

            setModels(
                models.map((model) => ({
                    value: model.name,
                    text: model.name,
                    maxOutput: model.max_output,
                }))
            );
        };
        fetchModels();
    }, []);

    const onSubmit = async () => {
        const values = getValues();

        const dataToSend = {
            name: values.name,
            description: values.description,
            brain_type: "doc",
            snippet_color: values.snippet_color || "#ffffff",
            snippet_emoji: values.snippet_emoji || "🧠",
            status: values.status,
            model: values.model,
            max_tokens: values.max_tokens,
        };

        const formData = new FormData();
        Object.entries(dataToSend).forEach(([key, value]) => {
            formData.append(key, value);
        });

        setLoading(true);

        let response, error;

        if (data.id !== undefined) {
            response = await brainService.updateBrain(data.id, formData);
        } else {
            response = await brainService.createBrain(dataToSend);
        }

        setLoading(false);

        if (error || !response) {
            console.error("Error during update or creation:", error || "Missing response data");
            return;
        }
        if (onSend) {
            onSend(response[0]);
        }
    };

    return (
        <div>
            <Form className="edit-container" onSubmit={handleSubmit(onSubmit)}>
                <Form.Title>{form.title || "Create New Brain"}</Form.Title>
                <Form.Body className="body-editModal-container">
                    <div style={{ display: "block" }}>
                        <Field label="Brain Name" />
                        <TextInput
                            {...register("name", { required: true })}
                            style={errors.name ? { borderColor: "red" } : {}}
                            placeholder="Enter the name of your brain"
                        />
                    </div>

                    <div className="modal-bar" style={{ display: "block" }}>
                        <Field label="Description" />
                        <TextInput
                            {...register("description", { required: true })}
                            style={errors.description ? { borderColor: "red" } : {}}
                            placeholder="Enter a description"
                        />
                    </div>

                    <div className="flex modal-bar choice-models">
                        <div style={{ display: "block" }}>
                            <Field label="Model" />
                            <DropDown
                                choices={models.map(({ value, text }) => ({ value, text }))}
                                placeholder={data.model || "Select a model"}
                                defaultChoice={{
                                    value: data.model || "",
                                    text: models.find((model) => model.value === data.model)?.text || "Select a model",
                                }}
                                onChange={(choice) => handleModelChange(choice.value)}
                                onBlur={() => setValue("model", model)}
                            />
                        </div>

                        <div style={{ display: "block" }}>
                            <div className="flex">
                                <Field label="Max Tokens:" /> <span className="max-tokens">{maxTokens}</span>
                            </div>
                            <input
                                type="range"
                                min={100}
                                max={defineMaxTokens(model)}
                                step={100}
                                {...register("max_tokens", { required: true })}
                                value={maxTokens}
                                onChange={(e) => setValue("max_tokens", e.target.value)}
                            />
                        </div>
                    </div>
                </Form.Body>

                <Form.Buttons>
                    <ButtonAction
                        type="submit"
                        label={data.id ? "Update" : "Add"}
                        disabled={!isValid}
                        onSubmit={onSubmit}></ButtonAction>
                </Form.Buttons>
            </Form>
        </div>
    );
}

export default FormBrain;
